import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import * as fe from "@runn/filter-engine"

import {
  people_bool_exp,
  projects_bool_exp,
} from "../FilteredQuery/__generated__/FilterQuery.graphql"

import {
  AllowedPeopleFilter,
  AllowedProjectFilter,
} from "~/helpers/filter-engine"

import {
  buildPeopleFilter,
  buildProjectsFilter,
} from "~/Planner/buildHasuraFilter"
import { ViewID, VirtualViewAlias } from "~/Views/ViewsProvider"

type DeepWriteable<T> = { -readonly [P in keyof T]: DeepWriteable<T[P]> }

export type TemporaryPlannerFilter = {
  projectFilters: DeepWriteable<projects_bool_exp> | null
  peopleFilters: DeepWriteable<people_bool_exp> | null
}

export type TemporaryFilterSliceState = {
  // Additional IDs that are required on the planner as the result of an action from a temporary view
  additionalPlannerIds: Record<
    number,
    {
      projectIds: number[]
      peopleIds: number[]
    }
  >
  // Temporary filters that are used in a component using independent views
  temporaryPlannerFilter: TemporaryPlannerFilter
}

const getInitialState = (): TemporaryFilterSliceState => {
  return {
    additionalPlannerIds: {},
    temporaryPlannerFilter: {
      peopleFilters: null,
      projectFilters: null,
    },
  }
}
const temporaryFilterSlice = createSlice({
  name: "temporaryFilter",
  initialState: getInitialState,
  reducers: {
    setTemporaryPlannerFilter: (
      state,
      action: PayloadAction<{
        databaseId?: number
        alias?: VirtualViewAlias
        peopleFilters: AllowedPeopleFilter
        projectFilters: AllowedProjectFilter
      }>,
    ) => {
      // @eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion

      const peopleFilters = buildPeopleFilter(
        fe.filters.flat(action.payload.peopleFilters),
      )
      const projectFilters = buildProjectsFilter(
        fe.filters.flat(action.payload.projectFilters),
      )

      state.temporaryPlannerFilter = {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        peopleFilters: peopleFilters as DeepWriteable<people_bool_exp>,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        projectFilters: projectFilters as DeepWriteable<projects_bool_exp>,
      }
    },
    resetTemporaryPlannerFilter: (state) => {
      state.temporaryPlannerFilter = {
        peopleFilters: null,
        projectFilters: null,
      }
    },
    addAdditionalPlannerIds: (
      state,
      action: PayloadAction<{
        viewId: ViewID
        projectIds?: number[]
        peopleIds?: number[]
      }>,
    ) => {
      state.additionalPlannerIds[action.payload.viewId] ??= {
        projectIds: [],
        peopleIds: [],
      }

      const current = state.additionalPlannerIds[action.payload.viewId]
      state.additionalPlannerIds[action.payload.viewId] = {
        projectIds: [
          ...current.projectIds,
          ...(action.payload?.projectIds ?? []),
        ],
        peopleIds: [...current.peopleIds, ...(action.payload?.peopleIds ?? [])],
      }
    },
  },
})

const { actions, reducer } = temporaryFilterSlice
export const {
  setTemporaryPlannerFilter,
  resetTemporaryPlannerFilter,
  addAdditionalPlannerIds,
} = actions
export default reducer
